import Auth from './Auth';
import CartUtils from './CartUtils';
import checkoutVariant from './checkoutVariant';
import generalHelpers from './generalHelpers';
import serviceUtils from './serviceUtils';

const UNIFIED_CHECKOUT_PATH = '/checkout';
const LEGACY_SINGLE_PAGE_PATH = '/checkout/start-membership';
const REVIEW_PATH = '/checkout/review';
const GUEST_REVIEW_PATH = '/guest/checkout/review';

/* Only the single-page checkouts read location.state.initCartInfo. The review
   route already carries a different state shape from corporate-portal. */
const SINGLE_PAGE_PATHS = [UNIFIED_CHECKOUT_PATH, LEGACY_SINGLE_PAGE_PATH];

/* The unified checkout has no notion of a company and sends companyCode: null,
   which the server refuses for a corporate account (purchaseCartUtils.js:158).
   Read from localStorage because that is where the review routes read it
   (components/review/index.js:58), not from the navigation. */
const isCorporateShopper = () =>
  !!Auth.getCompanyFromLocalStorage()?.companyCode;

/* Where checkout lives for this cart. Synchronous and network-free — every caller
   has already awaited the cart it passes in. */
const resolveCheckoutPath = (cart, { isLoggedIn = Auth.isLoggedIn() } = {}) => {
  if (checkoutVariant.showsUnifiedCheckout() && !isCorporateShopper()) {
    return UNIFIED_CHECKOUT_PATH;
  }

  if (
    checkoutVariant.showsLegacy2024Checkout() &&
    serviceUtils.isCartValidForSubscriptionOnlyCheckout(cart || {})
  ) {
    return LEGACY_SINGLE_PAGE_PATH;
  }

  return isLoggedIn ? REVIEW_PATH : GUEST_REVIEW_PATH;
};

/* A react-router location descriptor with UTMs applied. Owning the split here is
   the point: history v5 will not parse a query out of pathname, so every call
   site used to repeat it, and two of them dropped UTMs entirely. */
const buildCheckoutLocation = (path, { initCartInfo } = {}) => {
  const [pathname, search] = generalHelpers.appendStoredUTMs(path).split('?');
  return {
    pathname,
    search: search ? `?${search}` : '',
    ...(SINGLE_PAGE_PATHS.includes(pathname) ? { state: { initCartInfo } } : {}),
  };
};

const getCheckoutDestination = (cart, options) =>
  buildCheckoutLocation(resolveCheckoutPath(cart, options), {
    initCartInfo: cart,
  });

/* Add-to-cart sites only: does adding this item hand the shopper straight to
   checkout, or do we show the pre-checkout promo modal? The unified gate changes
   WHERE checkout is, not WHETHER add-to-cart jumps to it — a cart with a one-time
   product still gets the modal.

   hasSubscription is overridable because addSubscriptionToCart's guest branch
   returns the temp cart it read BEFORE adding (CartUtils.js:424 vs :438), so
   res.subscription is undefined for a first-time guest. home and
   select-subscription pass true explicitly and always have; folding them into the
   plain predicate would send every guest to the legacy modal. */
const goesStraightToCheckout = (
  cart,
  { hasSubscription = !!cart?.subscription } = {},
) => {
  if (
    !checkoutVariant.showsUnifiedCheckout() &&
    !checkoutVariant.showsLegacy2024Checkout()
  ) {
    return false;
  }

  return (
    hasSubscription &&
    serviceUtils.isCartValidForSubscriptionOnlyCheckout({
      ...(cart || {}),
      subscription: hasSubscription,
    })
  );
};

/* Whether Add To Cart opens the drawer instead of interrupting with a modal and,
   for a subscription, navigating straight to checkout. Tied to the unified arm:
   the drawer's Checkout button leads to the unified checkout, so offering it
   while the gate is off would hand the shopper a cart they cannot check out of
   the way it implies. bitsy keeps the modal. */
const usesCartDrawer = () => checkoutVariant.showsUnifiedCheckout();

/* Whether the destination depends on what is in the cart. With both gates off
   every cart goes to review, so callers can skip a fetch they would throw away —
   bitsy in particular never had one on this path. Call after ready(). */
const needsCart = () =>
  checkoutVariant.showsUnifiedCheckout() ||
  checkoutVariant.showsLegacy2024Checkout();

/* Whether a pathname is one of the checkouts, for the shell's chrome
   decisions. Anchored on the constant rather than a literal: /checkout is an
   exact match because every other checkout route is under it, and a bare
   includes('/checkout/') — what the footer used to test — stopped matching the
   unified checkout the moment it moved up to /checkout. */
const isCheckoutPath = (pathname = '') =>
  pathname === UNIFIED_CHECKOUT_PATH || pathname.includes('/checkout/');

/* Hold until Statsig can answer. checkGate returns false before init settles, and
   updateUserAsync on login drops it back to Loading — without this a click in
   either window silently routes to the checkout the shopper is not in. */
const ready = () => checkoutVariant.whenVariantResolved();

/* The "Go to Cart" buttons in the add-to-cart modals. These used to push straight
   to review, which was right while review was the only cart page — with the
   unified gate on it would strand the shopper on the checkout they are not in.
   Replaces generalHelpers.goToCart, which cannot call this without a cycle. */
const goToCart = async (props) => {
  if (props.onCartUpdate) props.onCartUpdate();
  await ready();
  const cart = needsCart() ? await CartUtils.getCart() : null;
  props.history.push(getCheckoutDestination(cart));
};

export default {
  resolveCheckoutPath,
  isCorporateShopper,
  goToCart,
  buildCheckoutLocation,
  getCheckoutDestination,
  goesStraightToCheckout,
  usesCartDrawer,
  needsCart,
  isCheckoutPath,
  ready,
  UNIFIED_CHECKOUT_PATH,
  LEGACY_SINGLE_PAGE_PATH,
  REVIEW_PATH,
  GUEST_REVIEW_PATH,
};
